import React, { useState } from 'react'
import { Card, Table, Alert, Spinner, Button, Form, Row, Col, ButtonGroup, Badge, Breadcrumb } from 'react-bootstrap'
import { useAdminMunicipalityBucketFiles } from 'api/hooks/use-admin-municipality-bucket-files'
import BucketFileSampleModal from './BucketFileSampleModal'

const BUCKETS = [
  { value: 'upload', label: 'Upload Bucket', municipalityField: 'bucket_name' },
  { value: 'output', label: 'Output Bucket', municipalityField: 'output_bucket' }
]

const formatSize = (bytes) => {
  if (bytes == null) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

// Strip a "folder/" prefix off a key for display. The full key stays in
// state — only the visible label is shortened.
const relativize = (key, prefix) => {
  if (!prefix) return key
  return key.startsWith(prefix) ? key.slice(prefix.length) : key
}

// Break a prefix like "data/2024/foo/" into breadcrumb segments:
// [{ label: 'data', prefix: 'data/' }, { label: '2024', prefix: 'data/2024/' }, ...]
const buildBreadcrumbs = (prefix) => {
  if (!prefix) return []
  const parts = prefix.split('/').filter(Boolean)
  let running = ''
  return parts.map((part) => {
    running += `${part}/`
    return { label: part, prefix: running }
  })
}

const MunicipalityBucketBrowser = ({ municipality }) => {
  const [bucket, setBucket] = useState('upload')
  // No default prefix — start at the bucket root and let the user navigate.
  const [prefixInput, setPrefixInput] = useState('')
  const [activePrefix, setActivePrefix] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)

  const bucketMeta = BUCKETS.find((b) => b.value === bucket)
  const bucketName = municipality[bucketMeta.municipalityField]

  const { data, isLoading, error, refetch, isFetching } = useAdminMunicipalityBucketFiles({
    municipalityId: municipality.id,
    bucket,
    prefix: activePrefix,
    enabled: !!bucketName
  })

  const navigateTo = (newPrefix) => {
    setPrefixInput(newPrefix)
    setActivePrefix(newPrefix)
  }

  const handleSwitchBucket = (value) => {
    setBucket(value)
    setSelectedFile(null)
    setPrefixInput('')
    setActivePrefix('')
  }

  const handleApplyPrefix = (e) => {
    e.preventDefault()
    // Normalize: if the user typed a path without a trailing slash, leave it
    // alone — they may want to filter by partial-name prefix rather than
    // navigate into a folder.
    setActivePrefix(prefixInput)
  }

  const breadcrumbs = buildBreadcrumbs(activePrefix)
  const hasContent = data && (data.prefixes?.length > 0 || data.objects?.length > 0)

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Bucket Files</h5>
        <ButtonGroup size="sm">
          {BUCKETS.map((b) => (
            <Button
              key={b.value}
              variant={bucket === b.value ? 'primary' : 'light'}
              className={bucket === b.value ? '' : 'text-primary'}
              onClick={() => handleSwitchBucket(b.value)}
            >
              {b.label}
            </Button>
          ))}
        </ButtonGroup>
      </Card.Header>
      <Card.Body>
        {!bucketName ? (
          <Alert variant="warning" className="mb-0">
            This municipality has no {bucketMeta.label.toLowerCase()} configured.
          </Alert>
        ) : (
          <>
            <div className="text-muted small mb-2">
              <code>{bucketName}</code>
            </div>

            <Form onSubmit={handleApplyPrefix} className="mb-2">
              <Row className="g-2 align-items-end">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Path</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={prefixInput}
                      placeholder="(root) — start typing or click a folder below"
                      onChange={(e) => setPrefixInput(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Button type="submit" variant="primary" size="sm" disabled={isFetching}>
                    Go
                  </Button>
                </Col>
                <Col md={2}>
                  <Button
                    type="button"
                    variant="light"
                    className="text-primary"
                    size="sm"
                    onClick={() => refetch()}
                    disabled={isFetching}
                  >
                    Refresh
                  </Button>
                </Col>
              </Row>
            </Form>

            <Breadcrumb className="mb-3 small">
              <Breadcrumb.Item
                active={!activePrefix}
                onClick={(e) => { e.preventDefault(); navigateTo('') }}
                href="#"
              >
                {bucketName}
              </Breadcrumb.Item>
              {breadcrumbs.map((crumb, idx) => {
                const isLast = idx === breadcrumbs.length - 1
                return (
                  <Breadcrumb.Item
                    key={crumb.prefix}
                    active={isLast}
                    onClick={isLast ? undefined : (e) => { e.preventDefault(); navigateTo(crumb.prefix) }}
                    href="#"
                  >
                    {crumb.label}
                  </Breadcrumb.Item>
                )
              })}
            </Breadcrumb>

            {isLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading files…</span>
                </Spinner>
              </div>
            )}
            {error && (
              <Alert variant="danger">
                {error.response?.data?.message || error.message || 'Failed to list files'}
              </Alert>
            )}
            {data && !isLoading && (
              <>
                {data.truncated && (
                  <Alert variant="info" className="py-2 small">
                    Listing was truncated. Narrow the path to see all entries.
                  </Alert>
                )}
                {!hasContent ? (
                  <Alert variant="info" className="mb-0">
                    {activePrefix ? 'This folder is empty.' : 'No objects found at the bucket root.'}
                  </Alert>
                ) : (
                  <Table bordered hover responsive size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th style={{ width: '8rem' }}>Size</th>
                        <th style={{ width: '12rem' }}>Last Modified</th>
                        <th style={{ width: '7rem' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.prefixes?.map((p) => (
                        <tr key={`prefix-${p}`} style={{ cursor: 'pointer' }} onClick={() => navigateTo(p)}>
                          <td>
                            <span role="img" aria-label="folder" className="me-2">📁</span>
                            <code>{relativize(p, activePrefix)}</code>
                          </td>
                          <td className="text-muted">—</td>
                          <td className="text-muted">—</td>
                          <td>
                            <Badge bg="light" text="primary">Folder</Badge>
                          </td>
                        </tr>
                      ))}
                      {data.objects.map((obj) => (
                        <tr key={obj.key}>
                          <td>
                            <span role="img" aria-label="file" className="me-2">📄</span>
                            <code>{relativize(obj.key, activePrefix)}</code>
                          </td>
                          <td>{formatSize(obj.size)}</td>
                          <td>{obj.last_modified ? new Date(obj.last_modified).toLocaleString() : '—'}</td>
                          <td>
                            {obj.sampleable ? (
                              <Button size="sm" variant="primary" onClick={() => setSelectedFile(obj.key)}>
                                Sample
                              </Button>
                            ) : (
                              <Badge bg="light" text="primary">N/A</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </>
            )}
          </>
        )}

        <BucketFileSampleModal
          municipalityId={municipality.id}
          bucket={bucket}
          fileKey={selectedFile}
          onClose={() => setSelectedFile(null)}
        />
      </Card.Body>
    </Card>
  )
}

export default MunicipalityBucketBrowser
